import dayjs from 'dayjs';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { getAllReversedCessionsNominees } from '../../../../../lib/api/reverseCession';
import { getSelectedCertificate } from '../../../../../lib/api/SIICertificates';
import GlobalSearcher, {
  Filter,
} from '../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../lib/designSystem/TableWithPagination';
import CessionDialog from './components/CessionDialog';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'folio', label: 'Folio' },
  { key: 'businessName', label: 'Cliente' },
  { key: 'businessIdentifier', label: 'RUT' },
  { key: 'stakeholderName', label: 'Deudor' },
  { key: 'stakeholderIdentifier', label: 'RUT' },
  { key: 'amount', label: 'Monto Documento', type: 'money' },
  { key: 'cessionedAt', label: 'Fecha de Cesión', type: 'date' },
];

const CessionsManagement: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [openCessionDialog, setOpenCessionDialog] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [hasCessionIntegration, setHasCessionIntegration] = useState(false);
  const [reversedNominees, setReversedNominees] = useState([]);
  const startDateDefault = new Date();
  startDateDefault.setMonth(startDateDefault.getMonth() - 1);
  const [startDate, setStartDate] = useState(dayjs(startDateDefault));
  const [endDate, setEndDate] = useState(dayjs(new Date()));
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchReversedCessions = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllReversedCessionsNominees(dispatch, query);
      setReversedNominees(response.data);
      setPagination(response.pagination);
    } catch (error) {
      alert('Error al cargar los pagos');
    }
  };

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha inicio',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      type: 'date',
      label: 'Fecha fin',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      type: 'text',
      label: 'RUT emisor',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'RUT deudor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchReversedCessions({ page: 1, pageSize: 10 })}
        />
      ),
      disabled: selectedCertificate === null || !hasCessionIntegration,
    },
    {
      action: () => setOpenCessionDialog(true),
      name: 'Ceder/Reversar',
      color: 'primary',
      disabled:
        !validatePermissions(user, 'financing_reversed_cession_new') ||
        selectedCertificate === null ||
        !hasCessionIntegration,
    },
  ];

  const setUpCertificates = async () => {
    try {
      const response = await getSelectedCertificate(dispatch);
      setSelectedCertificate(response);
    } catch (error) {
      console.log(error);
    }
  };

  const checkCessionIntegration = async () => {
    try {
      const response = await getIntegrationStatusByInternalName(
        dispatch,
        'recesion',
      );
      setHasCessionIntegration(response.status);
    } catch (error) {
      console.log(error);
    }
  };

  const setUpReversedCessions = async () => {
    setLoading(true);
    await fetchReversedCessions({ page: 0, pageSize: PAGE_SIZE });
    await setUpCertificates();
    await checkCessionIntegration();
    setLoading(false);
  };

  useEffect(() => {
    setUpReversedCessions();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las facturas reversadas y podrás cederlas a otras empresas."
        variant="body1"
        buttons={buttonsData}
      />
      <TableWithPagination
        dataHeaders={headers}
        data={reversedNominees}
        setData={setReversedNominees}
        rowsPerPageDefault={50}
        showActions
        exportToFile="xlsx"
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchReversedCessions}
      />
      {openCessionDialog && (
        <CessionDialog
          open={openCessionDialog}
          setOpen={setOpenCessionDialog}
          onSuccess={async () => {
            await fetchReversedCessions({ page: 0, pageSize: PAGE_SIZE });
          }}
        />
      )}
    </div>
  );
};

export default CessionsManagement;
